import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

// Accounts & Ledger mapping - targets nrgCore AccountLedgerController (LEDGER.usp_*).
const BASE = "/api/v1/sustina/account-ledger";
const config = { siteKey: process.env.REACT_APP_SITE_KEY };
const CHUNK = 2000;

const call = async (op, body) => {
    const rawData = await dbPayload(`${BASE}/${op}`, "POST", config.siteKey, GoldenSourceHelper.prepareForApi(body || {}));
    const res = GoldenSourceHelper.normalize(rawData);
    if (!res || !res.success) throw new Error(res?.message || `Account ledger ${op} failed.`);
    return res.data;
};

const list = async (op, body) => (await call(op, body)) || [];

/** Sends rows in chunks so a large ledger never exceeds one request. */
const importRows = async (op, companyId, fileName, rows, onProgress) => {
    let last = null;
    const parts = Math.max(1, Math.ceil(rows.length / CHUNK));
    for (let i = 0; i < parts; i++) {
        const slice = rows.slice(i * CHUNK, (i + 1) * CHUNK);
        last = await call(op, {
            CorporateEntityId: companyId,
            FileName: parts > 1 ? `${fileName} (part ${i + 1}/${parts})` : fileName,
            JsonRows: JSON.stringify(slice),
        });
        if (onProgress) onProgress(i + 1, parts);
    }
    return last;
};

export const importChartOfAccounts = (companyId, fileName, rows, onProgress) =>
    importRows("import-chart", companyId, fileName, rows, onProgress);

export const importLedgerLines = (companyId, fileName, rows, onProgress) =>
    importRows("import-ledger", companyId, fileName, rows, onProgress);

export const fetchImports = (companyId) => list("list-imports", { CorporateEntityId: companyId });
export const removeImport = (batchId) => call("remove-import", { BatchId: batchId });
export const fetchAccountCodes = (companyId) => list("list-codes", { CorporateEntityId: companyId });
export const fetchLedgerLines = (companyId, code) => list("list-lines", { CorporateEntityId: companyId, Code: code });
export const fetchMappings = (companyId, code) => list("list-mappings", { CorporateEntityId: companyId, Code: code });
export const fetchCodesForItem = (companyId, itemId) =>
    list("list-by-item", { CorporateEntityId: companyId, TaxonomyDetailRowId: itemId });
export const searchEsgItems = (search) => list("search-items", { Search: search });

export const setMapping = (companyId, code, itemId, valueBasisCode, factor, factorNote) =>
    call("set-mapping", {
        CorporateEntityId: companyId,
        Code: code,
        TaxonomyDetailRowId: itemId,
        ValueBasisCode: valueBasisCode,
        Factor: factor === "" || factor == null ? null : Number(factor),
        FactorNote: factorNote || null,
    });

export const removeMapping = (accountMappingId) => call("remove-mapping", { AccountMappingId: accountMappingId });

// ---------- reporting figures (automated from mapped codes, or typed in) ----------
export const setFigureMapping = (companyId, code, fieldPath, basis, factor) =>
    call("figure-map", {
        CorporateEntityId: companyId,
        Code: code,
        FieldPath: fieldPath || null,
        ValueBasisCode: basis || "QUANTITY",
        Factor: factor === "" || factor == null ? null : Number(factor),
    });

export const fetchFigures = (companyId, from, to) =>
    list("figures", { CorporateEntityId: Number(companyId), PeriodFrom: from, PeriodTo: to });

/** figures = [{ path, value, unit }] — a blank value clears that figure. */
export const saveManualFigures = (companyId, from, to, figures) =>
    list("manual-figures", { CorporateEntityId: Number(companyId), PeriodFrom: from, PeriodTo: to, FiguresJson: JSON.stringify(figures) });

// ---------- accounting connectors (global admin set-up) ----------
export const fetchConnectors = () => list("connectors/status", {});
export const saveConnector = ({ providerCode, clientId, clientSecret, isEnabled }) =>
    list("connectors/set", { ProviderCode: providerCode, ClientId: clientId || null, ClientSecret: clientSecret || null, IsEnabled: !!isEnabled });
